import {
  useDisclosure,
  useMarketAccountsContext,
  type MarketAccount,
  type Security,
  type UseDisclosureReturn,
} from '@talos/kyoko';
import { useMemo } from 'react';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { isActiveInternalTradingMarketAccount } from './customRateValidation';

interface UseCustomRateDialogsParams {
  security: Security | undefined;
  selectedMarketAccounts: string[];
}

type PublishDialogProps = UseDisclosureReturn & {
  internalMarketAccounts: MarketAccount[];
  initialMarketAccount?: MarketAccount;
  symbol: string;
};

type ClearDialogProps = UseDisclosureReturn & {
  marketAccount: MarketAccount;
  symbol: string;
};

/**
 * Builds Custom Rate dialog state for a market data card.
 *
 * The publish dialog can open only when the feature is enabled, the card has a
 * resolved security, and at least one eligible internal market account exists.
 */
export function useCustomRateDialogs({ security, selectedMarketAccounts }: UseCustomRateDialogsParams) {
  const { enablePublishCustomRate } = useFeatureFlag();
  const { marketAccountsByName, marketAccountsList } = useMarketAccountsContext();
  const dialog = useDisclosure();
  const clearDialog = useDisclosure();

  // Custom Rate publishing requires accounts to be active, internal, and trading type.
  const internalMarketAccounts = useMemo(
    () => marketAccountsList.filter(isActiveInternalTradingMarketAccount),
    [marketAccountsList]
  );
  const symbol = security?.Symbol;
  const canPublishCustomRate = enablePublishCustomRate && symbol != null && internalMarketAccounts.length > 0;

  const selectedInternalMarketAccount = getSelectedInternalMarketAccount(selectedMarketAccounts, marketAccountsByName);
  const canClearCustomRate = canPublishCustomRate && selectedInternalMarketAccount != null;

  const publishDialogProps: PublishDialogProps | undefined =
    dialog.isOpen && canPublishCustomRate
      ? {
          ...dialog,
          symbol,
          internalMarketAccounts,
          initialMarketAccount: selectedInternalMarketAccount,
        }
      : undefined;
  const clearDialogProps: ClearDialogProps | undefined =
    clearDialog.isOpen && canClearCustomRate
      ? {
          ...clearDialog,
          symbol,
          marketAccount: selectedInternalMarketAccount,
        }
      : undefined;

  return {
    canPublishCustomRate,
    canClearCustomRate,
    clearDialogProps,
    onOpenClearCustomRate: canClearCustomRate ? clearDialog.open : undefined,
    onOpenPublishCustomRate: canPublishCustomRate ? dialog.open : undefined,
    publishDialogProps,
  };
}

function getSelectedInternalMarketAccount(
  selectedMarketAccounts: string[],
  marketAccountsByName: Map<string, MarketAccount>
) {
  if (selectedMarketAccounts.length !== 1) {
    return undefined;
  }

  const marketAccount = marketAccountsByName.get(selectedMarketAccounts[0]);
  return marketAccount != null && isActiveInternalTradingMarketAccount(marketAccount) ? marketAccount : undefined;
}
